import { useState, useEffect, useCallback } from 'react';

// Storage key for web device fingerprint
const DEVICE_ID_STORAGE_KEY = 'blazepos-device-id';
const DEVICE_FINGERPRINT_STORAGE_KEY = 'blazepos-device-fingerprint';

export interface DeviceInfo {
  deviceId: string;
  deviceType: 'electron' | 'web' | 'mobile';
  fingerprint: DeviceFingerprint;
  isElectron: boolean;
}

export interface DeviceFingerprint {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory?: number;
  touchSupport: boolean;
  cookiesEnabled: boolean;
  doNotTrack: boolean;
  webGLRenderer?: string;
  canvasHash?: string;
}

// Cache both the completed fingerprint and the in-flight work. Several
// device-aware components can mount together, especially in React Strict Mode.
// Sharing the promise prevents each mount from fingerprinting independently.
let cachedFingerprint: { id: string; fingerprint: DeviceFingerprint } | null = null;
let fingerprintPromise: Promise<{ id: string; fingerprint: DeviceFingerprint }> | null = null;

/**
 * Generate a fingerprint hash from browser characteristics
 * This creates a reasonably stable identifier, though it can change
 * with browser updates or hardware changes
 */
async function generateBrowserFingerprint(): Promise<{ id: string; fingerprint: DeviceFingerprint }> {
  if (cachedFingerprint) return cachedFingerprint;
  if (fingerprintPromise) return fingerprintPromise;

  fingerprintPromise = (async () => {
    const fingerprint: DeviceFingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}x${window.devicePixelRatio || 1}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: (navigator as any).deviceMemory,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === '1',
    };

    // Generate canvas fingerprint (unique rendering characteristics)
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('BlazePOS Device', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('BlazePOS Device', 4, 17);
        fingerprint.canvasHash = canvas.toDataURL().slice(-50);
      }
    } catch {
      // Canvas not available
    }

    // Generate hash from fingerprint
    const fingerprintString = JSON.stringify(fingerprint);
    const hashBuffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(fingerprintString)
    );
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const id = 'web_' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 32);

    return { id, fingerprint };
  })();

  try {
    cachedFingerprint = await fingerprintPromise;
    return cachedFingerprint;
  } finally {
    fingerprintPromise = null;
  }
}

/**
 * Get or generate a persistent device ID
 * For web, we use a combination of fingerprint + localStorage UUID for stability
 */
async function getOrCreateDeviceId(): Promise<{ id: string; fingerprint: DeviceFingerprint }> {
  // Check if we have a stored device ID
  let storedId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  const storedFingerprintJson = localStorage.getItem(DEVICE_FINGERPRINT_STORAGE_KEY);

  // Generate current fingerprint
  const { id: currentFingerprintId, fingerprint: currentFingerprint } = await generateBrowserFingerprint();

  if (storedId && storedFingerprintJson) {
    try {
      const storedFingerprint = JSON.parse(storedFingerprintJson) as DeviceFingerprint;

      // Check if fingerprint is still similar (allow for minor changes)
      // Compare key characteristics that don't change often
      const isSimilar =
        storedFingerprint.platform === currentFingerprint.platform &&
        storedFingerprint.timezone === currentFingerprint.timezone &&
        storedFingerprint.hardwareConcurrency === currentFingerprint.hardwareConcurrency &&
        (!storedFingerprint.webGLRenderer || !currentFingerprint.webGLRenderer || storedFingerprint.webGLRenderer === currentFingerprint.webGLRenderer);

      if (isSimilar) {
        // Update fingerprint but keep same ID
        localStorage.setItem(DEVICE_FINGERPRINT_STORAGE_KEY, JSON.stringify(currentFingerprint));
        return { id: storedId, fingerprint: currentFingerprint };
      }
    } catch {
      // Invalid stored fingerprint, regenerate
    }
  }

  // Generate new device ID with random component for uniqueness
  const randomPart = crypto.randomUUID().slice(0, 8);
  const newId = `web_${currentFingerprintId.slice(4, 20)}_${randomPart}`;

  // Store for future visits
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, newId);
  localStorage.setItem(DEVICE_FINGERPRINT_STORAGE_KEY, JSON.stringify(currentFingerprint));

  return { id: newId, fingerprint: currentFingerprint };
}

/**
 * Hook to get the current device's unique identifier
 * Works for both Electron (hardware ID) and web (fingerprint) apps
 */
export function useDeviceId() {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDeviceId = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if we're in Electron - check multiple indicators
      const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI !== undefined;
      const hasIsElectronFlag = hasElectronAPI && window.electronAPI?.isElectron === true;
      const hasDeviceAPI = hasElectronAPI && typeof window.electronAPI?.device?.getMachineId === 'function';

      const isElectron = hasIsElectronFlag && hasDeviceAPI;

      if (isElectron && window.electronAPI) {
        // In Electron, get hardware machine ID via IPC
        const machineId = await window.electronAPI.device?.getMachineId();

        if (!machineId) {
          throw new Error('Failed to get machine ID from Electron');
        }

        setDeviceInfo({
          deviceId: `electron_${machineId}`,
          deviceType: 'electron',
          fingerprint: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screenResolution: `${screen.width}x${screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            colorDepth: screen.colorDepth,
            hardwareConcurrency: navigator.hardwareConcurrency || 0,
            touchSupport: false,
            cookiesEnabled: true,
            doNotTrack: false,
          },
          isElectron: true,
        });
      } else {
        // Web browser - generate fingerprint-based ID
        const { id, fingerprint } = await getOrCreateDeviceId();

        setDeviceInfo({
          deviceId: id,
          deviceType: 'web',
          fingerprint,
          isElectron: false,
        });
      }
    } catch (err) {
      console.error('Failed to get device ID:', err);
      setError(err instanceof Error ? err.message : 'Failed to identify device');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeviceId();
  }, [loadDeviceId]);

  // Function to clear stored device ID (for testing/admin purposes)
  const clearDeviceId = useCallback(() => {
    localStorage.removeItem(DEVICE_ID_STORAGE_KEY);
    localStorage.removeItem(DEVICE_FINGERPRINT_STORAGE_KEY);
    loadDeviceId();
  }, [loadDeviceId]);

  return {
    deviceId: deviceInfo?.deviceId ?? null,
    deviceType: deviceInfo?.deviceType ?? null,
    fingerprint: deviceInfo?.fingerprint ?? null,
    isElectron: deviceInfo?.isElectron ?? false,
    isLoading,
    error,
    clearDeviceId,
    reload: loadDeviceId,
  };
}

// Extend Window interface for Electron API
declare global {
  interface Window {
    electronAPI?: {
      isElectron: boolean;
      device?: {
        getMachineId: () => Promise<string>;
      };
      // ... other existing properties
    };
  }
}
